import { ChevronRight } from "Shared/appIcons";
import type { Breadcrumb } from "Shared/Layouts/components/types";
import { UnstyledLink } from "Shared/Routing/UnstyledLink";
import type { ReactNode } from "react";

type Props = {
	title: string;
	subtitle?: string;
	breadcrumbs?: Breadcrumb[];
	actions?: ReactNode;
	clientSwitcher?: ReactNode;
};

/**
 * Secondary row showing the current page's title, breadcrumbs, and actions.
 * Renders below `TopbarNav` on wide viewports; hidden below `md` because
 * `DrawerNav.Topbar` already shows the same info next to the hamburger.
 */
export const PageHeader = ({
	title,
	subtitle,
	breadcrumbs,
	actions,
	clientSwitcher,
}: Props) => {
	const hasBreadcrumbs = !!breadcrumbs && breadcrumbs.length > 0;

	return (
		<div className="border-border bg-card hidden min-h-14 flex-shrink-0 items-center border-b px-6 py-2 md:flex">
			<div className="flex min-w-0 flex-1 items-center">
				{hasBreadcrumbs ? (
					<nav className="mr-3 flex items-center gap-1.5">
						{breadcrumbs.flatMap((crumb, idx) => {
							const key = `${crumb.to ?? ""}::${crumb.label}`;
							const node = crumb.to ? (
								<UnstyledLink
									key={key}
									to={crumb.to}
									className="text-small text-muted-foreground hover:text-foreground no-underline"
								>
									{crumb.label}
								</UnstyledLink>
							) : (
								<span key={key} className="text-small text-muted-foreground">
									{crumb.label}
								</span>
							);
							if (idx === 0) return [node];
							return [
								<ChevronRight
									key={`sep-${key}`}
									className="text-muted-foreground h-3.5 w-3.5"
									strokeWidth={2}
								/>,
								node,
							];
						})}
					</nav>
				) : null}
				<div className="min-w-0">
					<h1 className="text-h2 text-foreground truncate font-semibold">
						{title}
					</h1>
					{subtitle ? (
						<div className="text-tiny text-muted-foreground mt-0.5 truncate">
							{subtitle}
						</div>
					) : null}
					{clientSwitcher ? (
						<div className="mt-1.5">{clientSwitcher}</div>
					) : null}
				</div>
			</div>

			{actions ? (
				<div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
			) : null}
		</div>
	);
};
